import { useTranslation } from 'react-i18next'
import { PiLockSimple, PiSignIn, PiUserSwitch } from 'react-icons/pi'
import useUiContext from '@/custolink-hooks/useUiContext'
import { PrimaryButton } from '@/custolink-views/asset_frontend/redesigns/formKit'

type Variant = 'design' | 'classic'

/**
 * Class sets per page variant. `design` paints with the design-2 tokens
 * (`--rda-*`), which only exist inside AssetShell; `classic` uses the classic
 * page's own vocabulary: a white card on the brand band, brand-coloured action.
 */
const STYLE: Record<
  Variant,
  {
    card: string
    mark: string
    title: string
    text: string
    account: string
    label: string
    hint: string
    button: string
  }
> = {
  design: {
    card:
      'rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] ' +
      'p-4 sm:p-5 text-[var(--rda-on-surface)]',
    mark:
      'flex h-11 w-11 items-center justify-center rounded-full sm:h-12 sm:w-12 ' +
      'bg-[var(--rda-accent-tint)] text-[var(--rda-accent-text)]',
    title: 'mt-3 text-[22px] font-bold leading-[1.1] tracking-[-0.03em] sm:mt-4 sm:text-[28px]',
    text: 'mt-1.5 text-[15px] leading-snug text-[var(--rda-muted)] sm:text-base',
    account: 'mt-4 rounded-lg bg-[var(--rda-accent-wash)] px-3 py-2.5',
    label: 'text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--rda-muted)]',
    hint: 'mt-3 rounded-lg bg-[var(--rda-neutral)] px-3 py-2.5 text-sm leading-snug text-[var(--rda-on-neutral)]',
    button: 'mt-5 min-h-12 rounded-lg sm:rounded-xl',
  },
  classic: {
    card: 'rounded-2xl bg-white p-4 text-black',
    mark:
      'flex h-11 w-11 items-center justify-center rounded-full ' +
      'bg-background-color-asset-frontend text-white',
    title: 'mt-3 text-2xl font-semibold leading-tight',
    text: 'mt-1.5 text-sm leading-snug text-gray-600',
    account: 'mt-4 rounded-xl bg-gray-100 px-3 py-2.5',
    label: 'text-[11px] font-bold uppercase tracking-[0.12em] text-gray-600',
    hint: 'mt-3 rounded-xl bg-amber-50 px-3 py-2.5 text-sm leading-snug text-amber-900',
    button:
      'mt-5 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl ' +
      'bg-background-color-asset-frontend px-4 py-3 text-sm font-semibold text-white ' +
      'hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
      'focus-visible:outline-background-color-asset-frontend',
  },
}

/**
 * What a visitor sees on a product the organisation hides behind Asset
 * Protection ("Hide whole asset"). The server sends no serial number, product
 * name or components in this state, so there is nothing else to show; the one
 * way forward is the account. Signing in, or switching to another account,
 * opens the page's existing Account drawer, and the view is requested again
 * for the new identity.
 */
export default function AssetProtectedSignIn({
  onSignIn,
  variant = 'design',
  className = '',
}: {
  onSignIn: () => void
  variant?: Variant
  className?: string
}) {
  const { t } = useTranslation()
  const user = useUiContext().assetFrontendAuthedUser
  const s = STYLE[variant]
  const shownEmail = user?.email || user?.emailUnverified || null
  const unverified = user?.emailUnverified != null

  const buttonLabel = user == null ? t('assetView.protected.signIn') : t('assetView.protected.switchAccount')
  const buttonIcon =
    user == null ? <PiSignIn aria-hidden className="h-5 w-5" /> : <PiUserSwitch aria-hidden className="h-5 w-5" />

  return (
    <section
      aria-labelledby="asset-protected-title"
      className={s.card + ' ' + className}
      style={variant === 'classic' ? { boxShadow: 'rgb(80 80 80 / 100%) 0px 5px 15px' } : undefined}
    >
      <span aria-hidden className={s.mark}>
        <PiLockSimple className="h-5 w-5 sm:h-6 sm:w-6" />
      </span>
      <h1 className={s.title + ' [text-wrap:balance]'} id="asset-protected-title">
        {t('assetView.protected.title')}
      </h1>

      {user == null ? (
        <p className={s.text}>{t('assetView.protected.body')}</p>
      ) : (
        <>
          <p className={s.text}>{t('assetView.protected.noAccess')}</p>
          {shownEmail != null ? (
            <div className={s.account}>
              <p className={s.label}>{t('assetView.protected.signedInAs')}</p>
              <p className="mt-1 text-sm font-medium [overflow-wrap:anywhere]">{shownEmail}</p>
            </div>
          ) : null}
          {unverified ? (
            <p className={s.hint} role="note">
              {t('assetView.protected.verifyEmail')}
            </p>
          ) : null}
        </>
      )}

      {variant === 'design' ? (
        <PrimaryButton className={s.button} type="button" onClick={onSignIn}>
          {buttonIcon}
          {buttonLabel}
        </PrimaryButton>
      ) : (
        <button className={s.button} type="button" onClick={onSignIn}>
          {buttonIcon}
          {buttonLabel}
        </button>
      )}
    </section>
  )
}
